<!-- src/WidthVariantOption.vue -->
<script>
  import { productStore } from '@/lib/productStore.js';
  import { emitter } from '@/main.js';
  import { setUniformSwatchWidths } from '@/lib/swatchWidths.js';
  import { cleanName, handleize, isTrimmed } from '@/lib/vue-helpers.js';
  import VueCookies from 'vue-cookies';

  export default {
    name: 'WidthVariantOption',
    computed: {
      cleanWidthName() {
        return name => cleanName(name);
      }
    },
    data() {
      return {
        colors: [],
        product_title: '',
        widths: [],
        selectedColor: '',
        selectedSize: '',
        selectedWidth: '',
        isWidthSelected: false,
        includedProductType: [],
        optionThreeName: '',
        isLoading: true,
        mini_pdp: false,
        isOutfitPicker: false,
        isMobileViewport: false,
        excludeSwatchWidth: false,
        pdpSection: null,
        product_handle: '',
        optionthreeorder: [],
        passedOptThree: '',
        previouslySelectedWidth: '',
        userPreferredProductWidth: '',
        variants: [],
        slideVariantImageTags: '',
        slideVariantImage: false,
        shiftFontToSmall: false,
        lastEmittedImage: null,
        comingSoonTag: window.siteGlobals.set_reminder_tag,
        imageNS: null,
        imageKey: null,
        imageNS2: null,
        imageKey2: null,
        variant_image_metafield_name: null,
        vimageNS: null,
        vimageKey: null,
        outfitPickerIndex: null,
        isBundleProduct: false,
        bundledProducts: [],
        activeBundleHandle: null,
        selectedBundledProductIndex: 0,
        originalProductData: null,
        isHiddenForBundle: false
      };
    },
    mounted() {
      this.product_title = this.$el.parentElement.getAttribute('data-product-name');
      this.imageLookUpMax = parseInt(this.$el.parentElement.getAttribute('data-imageLookUpMax'));
      this.product_handle = this.$el.parentElement.getAttribute('data-product-handle');
      this.includedProductType = this.$el.parentElement.getAttribute('data-includedProductType').split(',');
      this.mini_pdp = this.$el.parentElement.getAttribute('data-mini-pdp') === 'true';
      this.isOutfitPicker = this.$el.parentElement.getAttribute('data-is-outfit-picker') === 'true';
      this.excludeSwatchWidth = window.excludeSwatchWidth === 'true';
      this.show_image_3 = this.$el.parentElement.getAttribute('data-show_image_3');
      this.optionthreeorder = this.$el.parentElement.getAttribute('data-optionthreeorder')?.split(',') || [];
      this.slideVariantImageTags = this.$el.parentElement.getAttribute('data-slide-variant-image-tags') || '';
      this.slideVariantImage = this.$el.parentElement.getAttribute('data-slide-variant-image') === 'true';
      this.shiftFontToSmall = this.$el.parentElement.getAttribute('data-shift_font_to_small');
      this.pdpSection = this.$el.parentElement.closest('.product-single') || this.$el.parentElement.closest('.mini-product-single');

      if (this.isOutfitPicker) {
        this.pdpSection = this.$el.parentElement;
      }

      const queryString = window.location.search;
      const urlParams = new URLSearchParams(queryString);
      this.passedOptThree = urlParams.get('op3');
      this.userPreferredProductWidth = VueCookies.get('pdp-width');

      emitter.on('product-data-updated', this.handleProductData);
      emitter.on('plytix-product-data-ready', this.handlePlytixData);
      emitter.on('variant-color-selected', ({ color }) => {
        if (this.isOutfitPicker) return;
        this.selectedColor = color;
        this.updateAvailability();
        this.shouldHideSizes();
        this.$nextTick(() => this.setOptionThreeWidth());
      });
      emitter.on('pdp-cookie-updated', ({ type, value }) => {
        if (type === 'width') {
          this.userPreferredProductWidth = value;
        }
      });
      emitter.on('variant-size-selected', ({ size }) => {
        if (this.isOutfitPicker) return;
        this.selectedSize = size;
        this.updateAvailability();
        this.shouldHideSizes();
        this.$nextTick(() => this.setOptionThreeWidth());
      });
      emitter.on('bundled-product-selected', this.handleBundledProductSelected);

      const hasPlytixData = this.checkPlytixData();
      if (!hasPlytixData) {
        this.initializeData();
      }
      this.updateMobileViewport();

      if (this.isOutfitPicker) {
        this.$nextTick(() => {
          this.resolveOutfitPickerIndex();
        });
        emitter.on('outfit-picker-product-changed', this.handleOutfitPickerProductChanged);
        emitter.on('outfit-picker-size-changed', ({ blockIndex, size }) => {
          if (!this.outfitPickerIndex) this.resolveOutfitPickerIndex();
          if (this.outfitPickerIndex !== blockIndex) return;
          this.selectedSize = size;
          this.updateAvailability();
          this.shouldHideSizes();
          this.$nextTick(() => this.setOptionThreeWidth());
        });
      }
      document.addEventListener('theme:resize:width', this.handleSwatchResize);
    },
    beforeUnmount() {
      emitter.off('product-data-updated', this.handleProductData);
      emitter.off('plytix-product-data-ready', this.handlePlytixData);
      emitter.off('variant-color-selected');
      emitter.off('variant-size-selected');
      emitter.off('bundled-product-selected');
      emitter.off('pdp-cookie-updated');
      if (this.isOutfitPicker) {
        emitter.off('outfit-picker-product-changed', this.handleOutfitPickerProductChanged);
        emitter.off('outfit-picker-size-changed');
      }
      document.removeEventListener('theme:resize:width', this.handleSwatchResize);
    },
    methods: {
      widthOptionAriaLabel(widthAttr, available) {
        const width = widthAttr?.startsWith('w') ? widthAttr.slice(1).trim() : (widthAttr || '').trim();
        const parts = [];
        if (this.selectedColor) parts.push(`color ${this.selectedColor}`);
        if (this.selectedSize) parts.push(`size ${this.selectedSize}`);
        const context = parts.length ? ` in ${parts.join(' ')}` : '';
        const status = available ? 'available' : 'currently unavailable';
        return `${this.product_title}${context} width option: ${width} is ${status}`;
      },
      checkPlytixData() {
        if (window.productFamiliesData && Array.isArray(window.productFamiliesData) && window.productFamiliesData.length > 0) {
          const preloadedData = productStore.getPreloadedProductData(this.product_title);
          if (preloadedData && preloadedData.length > 0) {
            productStore.processAndEmitData(preloadedData, this.product_title);
            this.handleContentLoaded();
            return true;
          }
        }
        return false;
      },
      handlePlytixData(processedData) {
        if (processedData && Array.isArray(processedData) && processedData.length > 0) {
          const preloadedData = productStore.getPreloadedProductData(this.product_title);
          if (preloadedData && preloadedData.length > 0) {
            productStore.processAndEmitData(preloadedData, this.product_title);
            this.handleContentLoaded();
          }
        }
      },
      normalizeSmartTags(tagsString) {
        return (tagsString || '')
          .split(',')
          .map(tag => tag.trim().toLowerCase())
          .filter(Boolean);
      },
      shouldEnableSmartTagsFromPlytixTags(tags) {
        const smartTags = this.normalizeSmartTags(this.slideVariantImageTags);
        if (!smartTags.length || !Array.isArray(tags)) return null;
        const normalizedTags = tags.map(tag => (tag || '').toString().trim().toLowerCase());
        return smartTags.some(tag => normalizedTags.includes(tag));
      },
      applySmartTagsFromPlytix(data) {
        const currentTags = data?.colors?.[0]?.tags;
        const enabled = this.shouldEnableSmartTagsFromPlytixTags(data?.colors?.[0]?.tags);
        const loggingEnabled = productStore.isLoggingEnabled(document.getElementById('product-families-json-data')?.dataset.speedTracking === 'true');
        if (enabled === null) {
          if (loggingEnabled) console.log('[SmartTags][Width] Fallback to Liquid boolean', { productTitle: this.product_title, slideVariantImage: this.slideVariantImage, smartTags: this.slideVariantImageTags, plytixTags: currentTags });
          return;
        }
        this.slideVariantImage = enabled;
        if (loggingEnabled) console.log('[SmartTags][Width] Plytix tag match', { productTitle: this.product_title, enabled, smartTags: this.slideVariantImageTags, plytixTags: currentTags });
      },
      async initializeData() {
        try {
          await productStore.fetchProductData(this.product_title, window.Shopify.country, this.imageLookUpMax, parseInt(window.siteGlobals.pdp_productsByTitleLookup), this.imageNS, this.imageKey, this.imageNS2, this.imageKey2, this.vimageNS, this.vimageKey, null, null, this.product_handle);
        } catch (err) {
          console.error('PDP width data fetch failed:', err);
        } finally {
          this.handleContentLoaded();
        }
      },
      handleContentLoaded() {
        const loader = this.$el.parentElement.closest('.pdp-width-variant-options-container').querySelector('.pdp-variant-loader');
        if (loader) {
          loader.style.minHeight = '0';
          loader.style.display = 'none';
        }
      },
      handleProductData(data) {
        if (!data || !data.widths) return;
        if ((this.isOutfitPicker && data.productHandle && data.productHandle !== this.product_handle) || (data.productTitle && data.productTitle !== this.product_title)) {
          return;
        }

        this.originalProductData = data;
        this.isBundleProduct = Boolean(data.isBundleProduct);
        this.bundledProducts = data.bundledProducts || [];
        this.activeBundleHandle = data.activeBundleHandle || (this.isBundleProduct && this.bundledProducts.length ? this.bundledProducts[0].handle : this.product_handle);

        if (this.isBundleProduct) {
          this.hideWidthModule();
          return;
        }

        this.applyProductData(data);
      },
      hideWidthModule() {
        if (this.isHiddenForBundle) return;
        this.isHiddenForBundle = true;
        if (this.$el && this.$el.style) {
          this.$el.style.display = 'none';
        }
      },
      applyProductData(productData) {
        if (!productData) return;
        this.applySmartTagsFromPlytix(productData);
        this.colors = productData.colors || [];
        this.widths = this.sortOptionThreeData(productData.widths || []);
        this.optionThreeName = productData.optionThreeName;
        this.variants = productData.variants || [];
        this.isLoading = true;
        this.selectedWidth = '';
        this.isWidthSelected = false;

        const delay = this.isOutfitPicker ? 0 : 300;
        setTimeout(() => {
          this.setDefaultSelections();
          this.isLoading = false;
          this.$nextTick(() => {
            this.setOptionThreeWidth();
            this.updateAvailability();
            if (this.selectedWidth && Array.isArray(this.widths) && this.widths.includes(this.selectedWidth)) {
              this.setWidthSelection(this.selectedWidth);
            }
          });
        }, delay);
      },
      setOptionThreeWidth() {
        if (!this.$el?.querySelectorAll) return;
        // Custom option3 swatches keep their merchant-configured fixed width.
        const optionButtons = this.$el.querySelectorAll('.variant-spacing.option-size-3 .p-options.option2');
        setUniformSwatchWidths(optionButtons, this.excludeSwatchWidth);
      },
      handleSwatchResize() {
        this.updateMobileViewport();
        this.$nextTick(() => this.setOptionThreeWidth());
      },
      handleBundledProductSelected() {
        if (!this.isBundleProduct) {
          return;
        }
        this.hideWidthModule();
      },
      handleOutfitPickerProductChanged({ blockIndex, productTitle, productHandle }) {
        if (!this.isOutfitPicker) return;
        if (!this.outfitPickerIndex) this.resolveOutfitPickerIndex();
        if (this.outfitPickerIndex !== blockIndex) return;
        if (!productTitle) return;
        this.product_title = productTitle;
        this.product_handle = productHandle || '';
        this.selectedWidth = this.previouslySelectedWidth || '';
        this.isWidthSelected = !!this.selectedWidth;
        if (!this.isLoading) {
          this.isLoading = true;
        }
        this.initializeData();
      },
      setWidthSelection(width) {
        if (isTrimmed(width.toString())) {
          width = width.toString();
        } else {
          width = width.trim();
        }

        this.selectedWidth = width;
        this.isWidthSelected = true;
        this.previouslySelectedWidth = width;

        [...this.pdpSection.querySelectorAll('[data-width]')].forEach(el => {
          el.classList.remove('selected');
        });

        const widthElements = this.pdpSection.querySelectorAll(`[data-width='w${CSS.escape(width)}']`);
        widthElements.forEach(el => {
          el.classList.add('selected');
        });

        const hasAvailableVariant = this.variants.some(v => v.availableForSale && v.width === width && v.color === this.selectedColor);

        if (hasAvailableVariant) {
          VueCookies.set('pdp-width', width);
          emitter.emit('pdp-cookie-updated', { type: 'width', value: width });
        }

        emitter.emit('variant-width-selected', { width });

        if (this.isOutfitPicker) {
          if (!this.outfitPickerIndex) {
            this.resolveOutfitPickerIndex();
          }
          emitter.emit('outfit-picker-width-changed', {
            blockIndex: this.outfitPickerIndex,
            width
          });
        }

        this.updateAvailability();
        this.handleSlideVariantImage(width);
      },
      getOptionImage(opt, optName) {
        if (opt === 'option3' && this.show_image_3 === 'true') {
          return 'background-image:url(' + window.cdn + handleize(optName) + '.png)';
        }
      },
      sortOptionThreeData(passed) {
        return passed.sort((a, b) => this.optionthreeorder.indexOf(a) - this.optionthreeorder.indexOf(b));
      },
      setDefaultSelections() {
        let effectiveSelectedColor = this.selectedColor;
        let effectiveSelectedSize = this.selectedSize;

        if (this.isOutfitPicker) {
          try {
            const selectedColorEl = this.pdpSection?.querySelector('.color-variant-options [data-color].selected');
            const selectedSizeEl = this.pdpSection?.querySelector('.size-variant-options [data-size].selected');
            if (selectedColorEl) {
              effectiveSelectedColor = selectedColorEl.getAttribute('data-color');
            }
            if (selectedSizeEl) {
              const raw = selectedSizeEl.getAttribute('data-size');
              effectiveSelectedSize = raw.startsWith('s') ? raw.slice(1) : raw;
            }
          } catch (e) {
            console.error('Error getting selected size and color:', e);
          }
          if (!effectiveSelectedColor && this.colors.length) {
            const byHandle = this.product_handle ? this.colors.find(c => c.handle === this.product_handle) : null;
            effectiveSelectedColor = (byHandle && byHandle.name) || this.colors[0].name;
          }
        }

        const potentialVariants = [];
        if (this.isOutfitPicker && this.previouslySelectedWidth) potentialVariants.push(this.previouslySelectedWidth);
        if (this.passedOptThree) potentialVariants.push(this.passedOptThree);
        if (this.userPreferredProductWidth) potentialVariants.push(this.userPreferredProductWidth);

        // Choose first variant that exists for this product
        let chosen = potentialVariants.find(w => Array.isArray(this.widths) && this.widths.includes(w));

        // If no variant is found, pick the first available given color/size
        if (!chosen) {
          chosen = this.findFirstAvailableWidth(effectiveSelectedColor, effectiveSelectedSize);
        }

        // Final fallback: first width in list
        if (!chosen && Array.isArray(this.widths) && this.widths.length > 0) {
          chosen = this.widths[0];
        }

        if (chosen) {
          this.setWidthSelection(chosen);
        }

        this.updateAvailability();
      },
      findFirstAvailableWidth(effectiveSelectedColor, effectiveSelectedSize) {
        if (!Array.isArray(this.widths) || !Array.isArray(this.variants)) return '';
        for (const width of this.widths) {
          const availableVariant = this.variants.find(v => v && v.width === width && v.availableForSale && (!effectiveSelectedColor || v.color === effectiveSelectedColor) && (!effectiveSelectedSize || !v.size || v.size === effectiveSelectedSize));
          if (availableVariant) return width;
        }
        return '';
      },
      isExcludedDefaultOption(name) {
        if (this.includedProductType.includes(name)) {
          return 'option p-options option3';
        }
        return 'option p-options option2';
      },
      resolveOutfitPickerIndex() {
        try {
          const productInner = this.$el.closest('.product__inner');
          const container = this.$el.closest('.op-width-options');
          const siblings = productInner ? Array.from(productInner.querySelectorAll('.op-width-options')) : [];
          const idx0 = siblings.findIndex(el => el === container);
          this.outfitPickerIndex = idx0 >= 0 ? idx0 + 1 : null;
        } catch (e) {
          this.outfitPickerIndex = null;
        }
      },
      handleSlideVariantImage(width) {
        const loggingEnabled = productStore.isLoggingEnabled(document.getElementById('product-families-json-data')?.dataset.speedTracking === 'true');
        if (!this.slideVariantImage) {
          if (loggingEnabled) console.log('[SmartTags][Width] Slide disabled', { productTitle: this.product_title, selectedColor: this.selectedColor, selectedSize: this.selectedSize, selectedWidth: width });
          return;
        }

        const currentVariant = this.variants.find(variant => variant.color === this.selectedColor && variant.size === this.selectedSize && variant.width === width);

        if (currentVariant && currentVariant.image) {
          if (loggingEnabled) console.log('[SmartTags][Width] Emit variant-updated', { productTitle: this.product_title, selectedColor: this.selectedColor, selectedSize: this.selectedSize, selectedWidth: width, image: currentVariant.image });
          if (this.lastEmittedImage !== currentVariant.image) {
            this.lastEmittedImage = currentVariant.image;
            setTimeout(() => {
              emitter.emit('variant-updated', {
                image: currentVariant.image,
                width: width,
                preferWidthSlug: true
              });
            }, 200);
          }
        } else {
          if (loggingEnabled) console.log('[SmartTags][Width] No variant image match', { productTitle: this.product_title, selectedColor: this.selectedColor, selectedSize: this.selectedSize, selectedWidth: width, hasVariant: !!currentVariant, variantImage: currentVariant?.image || null });
        }
      },
      updateAvailability() {
        let colorToUse = this.selectedColor;
        const selectedColorEl = this.pdpSection?.querySelector('.color-variant-options [data-color].selected');
        if (selectedColorEl) {
          colorToUse = selectedColorEl.getAttribute('data-color') || '';
        }
        if (!colorToUse && Array.isArray(this.colors) && this.colors.length) {
          const byHandle = this.product_handle ? this.colors.find(c => c.handle === this.product_handle) : null;
          colorToUse = (byHandle && byHandle.name) || this.colors[0].name || '';
        }

        let sizeToUse = this.selectedSize;
        const selectedSizeEl = this.pdpSection?.querySelector('.size-variant-options [data-size].selected');
        if (selectedSizeEl) {
          const raw = selectedSizeEl.getAttribute('data-size') || '';
          sizeToUse = raw.startsWith('s') ? raw.slice(1) : raw;
        }

        if (!colorToUse) return;

        const variants = this.variants || [];

        if (sizeToUse) {
          const hasSizeForColor = variants.some(v => v && v.color === colorToUse && (!v.size || v.size === sizeToUse));
          if (!hasSizeForColor) sizeToUse = '';
        }

        const availableVariants = variants.filter(v => v && v.availableForSale && v.color === colorToUse && (!sizeToUse || !v.size || v.size === sizeToUse) && v.width);
        let enabledWidths = new Set(availableVariants.map(v => v.width));

        if (enabledWidths.size === 0 && !sizeToUse) {
          enabledWidths = new Set(variants.filter(v => v && v.availableForSale && v.color === colorToUse && v.width).map(v => v.width));
        }

        this.disableAllWidthOptions();
        enabledWidths.forEach(w => this.setWidthEnabled(w));
      },
      disableAllWidthOptions() {
        [...this.pdpSection.querySelectorAll('[data-width]')].forEach(el => {
          el.classList.add('unavailable');
          el.setAttribute('aria-label', this.widthOptionAriaLabel(el.getAttribute('data-width'), false));
        });
      },
      setWidthEnabled(width) {
        [...this.pdpSection.querySelectorAll(`[data-width='w${CSS.escape(width)}']`)].forEach(el => {
          el.classList.remove('unavailable');
          el.setAttribute('aria-label', this.widthOptionAriaLabel(el.getAttribute('data-width'), true));
        });
      },
      shouldHideSizes() {
        const hideSizes = window.siteGlobals.set_reminder_pdp_hide_sizes === 'true';
        let comingSoon = false;
        if (this.colors && this.selectedColor) {
          const color = this.colors.find(color => color.name === this.selectedColor);
          if (color && color.tags && color.tags.includes(this.comingSoonTag)) {
            comingSoon = true;
            [...this.pdpSection.querySelectorAll(`[data-color='${CSS.escape(this.selectedColor)}']`)].forEach(element => {
              element.classList.remove('unavailable');
              const colorName = element.getAttribute('data-color') || this.selectedColor;
              const sizeContext = this.selectedSize ? ` in size ${this.selectedSize}` : '';
              element.setAttribute('aria-label', `${this.product_title}${sizeContext} color option: ${colorName} is available`);
            });
          }
        }
        return hideSizes && comingSoon;
      },
      isMobile() {
        return window.innerWidth <= window.theme?.sizes.medium;
      },
      updateMobileViewport() {
        this.isMobileViewport = this.isMobile();
      }
    }
  };
</script>

<template>
  <div v-if="!isBundleProduct && (isOutfitPicker || !isLoading) && !shouldHideSizes()">
    <div class="width-variant-options">
      <div class="width-wrapper" v-if="widths.length">
        <p :class="'option-title' + (mini_pdp ? ' mini-pdp-option-title' : '')">
          <span v-html="optionThreeName" class="option-name gt-p__small"></span>
          <span class="gt-p__small">:&nbsp;</span>
          <span v-html="cleanWidthName(selectedWidth)" class="gt-p__small"></span>
        </p>
      </div>
      <div class="variant-spacing option-size-3 variant-spacing-size" :class="{ 'gt-p__small': shiftFontToSmall == 'true' }">
        <div v-for="(width, index) in widths" :key="'width' + index" @click="setWidthSelection(width)">
          <button type="button" v-html="cleanWidthName(width)" :class="isExcludedDefaultOption(optionThreeName) + (isOutfitPicker && isMobileViewport ? ' gt-p__small' : '')" :style="getOptionImage('option3', width)" :data-width="'w' + width"></button>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
  .width-variant-options {
    .variant-spacing {
      display: flex;
      flex-wrap: wrap;
      row-gap: 5px;
    }

    .width-wrapper {
      display: flex;
      width: 100%;
      margin-top: 10px;
      align-items: center;
    }
  }
</style>
